/**
 * 种族目录类型定义
 *
 * 定义"种族"作为人物基础模型中的先天轴。
 *
 * 设计目标：
 * - 承担一个种族的"出厂可能性"：基线属性、属性范围、可习得技能池、
 *   外观模板、共享世界书。
 * - 不承担"剧情定位"概念（boss/npc/敌友），剧情概念由外部系统描述。
 */
import { z } from 'zod';
import { AttributesSchema } from './characterEntity';

// ============================================
// 属性范围
// ============================================

/**
 * 单条属性的取值上下限
 */
export const AttributeBandSchema = z.tuple([z.number(), z.number()]);
export type AttributeBand = z.infer<typeof AttributeBandSchema>;

/**
 * 六维属性范围（每条属性可选；缺省维度由实例化逻辑兜底）
 *
 * key 期望取自 ATTRIBUTE_KEYS，但运行时与现有 AttributeBonusesSchema 一致采用
 * `z.record(z.string(), ...)` 形式，便于与外部 JSON 数据互通且支持空默认。
 */
export const AttributeBandsSchema = z.record(z.string(), AttributeBandSchema);
export type AttributeBands = z.infer<typeof AttributeBandsSchema>;

// ============================================
// 外观模板
// ============================================

/**
 * 种族外观模板
 *
 * 用于在实例化时按权重抽取一个外观骨架。
 */
export const BodyTemplateSchema = z.object({
  /** 模板 ID，便于复现与调试 */
  id: z.string().min(1),
  /** 抽取权重 */
  weight: z.number().min(0).default(1),
  /** 身高描述 */
  height: z.string().default(''),
  /** 体重描述 */
  weight_desc: z.string().default(''),
  /** 体型描述 */
  figure: z.string().default(''),
  /** 头发描述 */
  hair: z.string().default(''),
  /** 眼睛描述 */
  eyes: z.string().default(''),
  /** 显著特征描述 */
  features: z.string().default(''),
});
export type BodyTemplate = z.infer<typeof BodyTemplateSchema>;

// ============================================
// 天然关系偏置
// ============================================

/**
 * 种族天然关系偏置规则
 *
 * 语义：
 * - 站在“本种族成员”的视角，看待“对方种族 / 标签”时的天然倾向
 * - 这是关系系统的静态基线，不包含剧情历史、装备伪装、声望等动态修正
 * - 命中多条规则时允许叠加，便于表达“既是人形又是人类”这类更强烈的偏见
 */
export const RaceRelationBiasRuleSchema = z.object({
  /** 规则 ID，便于调试与日志定位 */
  id: z.string().min(1).optional(),
  /** 对这些目标种族 ID 命中时应用 */
  towardRaceIds: z.array(z.string()).default([]),
  /** 对这些目标标签命中时应用 */
  towardTags: z.array(z.string()).default([]),
  /** 天然好感修正值，范围与 RelationState.affinity 对齐 */
  affinityDelta: z.number().min(-100).max(100),
});
export type RaceRelationBiasRule = z.infer<typeof RaceRelationBiasRuleSchema>;

// ============================================
// 种族目录条目
// ============================================

/**
 * 种族目录条目（统一重构后的完整定义）
 */
export const RaceDefinitionSchema = z.object({
  /** 种族 ID（小写英文） */
  id: z.string().min(1),
  /** 种族显示名（中文） */
  label: z.string().min(1),
  /** 种族图标（emoji 或图片 URL） */
  icon: z.string().default(''),
  /** 简短描述（用于选择器列表） */
  shortDesc: z.string().default(''),
  /** 完整描述（用于角色背景与世界书） */
  description: z.string().default(''),

  // ========== 创角与 NPC 可见性 ==========
  /** 是否在玩家创角列表中隐藏（怪物 / 敌对种族应为 true） */
  hiddenInCreation: z.boolean().optional().default(false),
  /** 是否禁止作为普通 NPC 实例化（结构敏感种族应为 true，预留位） */
  excludeFromNPC: z.boolean().optional().default(false),

  // ========== 基线与边界 ==========
  /** 种族基线属性（哥布林低，龙高） */
  baseAttributes: AttributesSchema,
  /** 属性合理上下限（防止极端骰出现） */
  attributeBands: AttributeBandsSchema.default({}),

  // ========== 能力池 ==========
  /** 可习得技能池（实例化时从该池抽取） */
  skillPool: z.array(z.string()).default([]),

  // ========== 外观 ==========
  /** 外观模板池（实例化时按权重抽取一个并叠加 tag 提示） */
  bodyTemplates: z.array(BodyTemplateSchema).default([]),

  // ========== Tag 与世界书 ==========
  /** 该种族个体默认拥有的 tag（如 entity_kind 或种族标识） */
  defaultTags: z.array(z.string()).default([]),
  /** 该种族成员对外界目标的天然关系偏置 */
  relationBiases: z.array(RaceRelationBiasRuleSchema).default([]),
  /** 共享世界书条目 ID */
  loreWorldBookId: z.string().optional(),
});
export type RaceDefinitionInput = z.input<typeof RaceDefinitionSchema>;
export type RaceDefinition = z.infer<typeof RaceDefinitionSchema>;
